import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Edit, Save, X, AlertCircle } from "lucide-react";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { CategoryTableRow } from "./CategoryTableRow";
import { CategoryWithTags, CategoryRule, TagCondition } from "../types/categoryRules";
import { useLevelAppCategories } from "../hooks/useLevelAppCategories";

interface LevelAppsCategoriesTableProps {
  levelId: string;
  appFilter?: any;
}

// Helper function to check if a value is a Convex wrapped undefined
const isConvexUndefined = (value: any): boolean => {
  return value && typeof value === 'object' && value._type === 'undefined';
};

// Helper function to clean Convex wrapped values
const cleanConvexValue = (value: any): any => {
  if (isConvexUndefined(value)) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.map(cleanConvexValue);
  }
  if (typeof value === 'object' && value !== null) {
    const cleaned: any = {};
    for (const [key, val] of Object.entries(value)) {
      cleaned[key] = cleanConvexValue(val);
    }
    return cleaned;
  }
  return value;
};

export const LevelAppsCategoriesTable = ({ levelId, appFilter }: LevelAppsCategoriesTableProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [localRules, setLocalRules] = useState<Record<string, CategoryRule>>({});
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  
  const { toast } = useToast();
  const { categories, updateAppFilter, getCategoryStatus, isLoading } = useLevelAppCategories(appFilter, isEditMode);

  // Create the initial rules map from appFilter prop with proper ID handling
  const initialRulesMap = useMemo(() => {
    if (!categories.length) return {};
    
    console.log('[initialRulesMap] Creating initial rules map');
    console.log('[initialRulesMap] Categories:', categories.length);
    console.log('[initialRulesMap] AppFilter:', appFilter);
    
    // Clean the appFilter to remove any Convex wrapped values
    const cleanedAppFilter = cleanConvexValue(appFilter) || {};
    console.log('[initialRulesMap] Cleaned appFilter:', cleanedAppFilter);
    
    const rulesMap: Record<string, CategoryRule> = {};
    
    categories.forEach(category => {
      // Ensure we have a valid category ID
      const categoryId = category._id;
      if (!categoryId || typeof categoryId !== 'string') {
        console.error('[initialRulesMap] Invalid category ID for category:', category.name, categoryId);
        return; // Skip this category
      }
      
      const status = getCategoryStatus(category.name);
      const filterRule = cleanedAppFilter[category.name];
      
      console.log(`[initialRulesMap] Category ${category.name} (ID: ${categoryId}): status=${status}, filterRule=`, filterRule);
      
      // Initialize rule structure based on current status
      let ruleData: CategoryRule = {
        category_id: categoryId,
        level_id: levelId,
        status: status as any,
        rule_type: undefined,
        selected_tag_ids: [],
        logic_operator: 'any_of',
        exception_tag_ids: [],
        exception_logic_operator: 'any_of',
        conditions: []
      };

      // If it's enabled with rules, extract the rule details
      if (status === 'enabled_with_rules' && typeof filterRule === 'object' && filterRule !== null) {
        const includeRule = filterRule.include;
        const excludeRule = filterRule.exclude;
        
        if (includeRule) {
          ruleData.rule_type = 'include_with';
          ruleData.selected_tag_ids = includeRule.tags || [];
          ruleData.logic_operator = includeRule.type === 'all' ? 'all_of' : 'any_of';
        }
        
        if (excludeRule) {
          ruleData.exception_tag_ids = excludeRule.tags || [];
          ruleData.exception_logic_operator = excludeRule.type === 'all' ? 'all_of' : 'any_of';
        }
        
        // Create the condition structure
        ruleData.conditions = [{
          id: 'main',
          ruleType: ruleData.rule_type || 'include_with',
          selectedTagIds: ruleData.selected_tag_ids,
          logicOperator: ruleData.logic_operator,
          exceptionTagIds: ruleData.exception_tag_ids,
          exceptionLogicOperator: ruleData.exception_logic_operator
        }];
      } else if (status === 'enabled_with_rules') {
        // Default structure for enabled_with_rules without specific rules
        ruleData.rule_type = 'include_with';
        ruleData.conditions = [{
          id: 'main',
          ruleType: 'include_with',
          selectedTagIds: [],
          logicOperator: 'any_of',
          exceptionTagIds: [],
          exceptionLogicOperator: 'any_of'
        }];
      }
      
      rulesMap[categoryId] = ruleData;
    });
    
    console.log('[initialRulesMap] Final rules map with proper IDs:', rulesMap);
    return rulesMap;
  }, [categories, appFilter, levelId, getCategoryStatus]);

  // Initialize local rules only when the initial map changes
  useEffect(() => {
    if (Object.keys(initialRulesMap).length > 0) {
      console.log('[useEffect] Initializing local rules with:', initialRulesMap);
      setLocalRules(initialRulesMap);
    }
  }, [initialRulesMap]);

  const handleEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setValidationErrors(new Set());
    // Reset local rules to initial state
    setLocalRules(initialRulesMap);
  };

  const validateRules = () => {
    const errors = new Set<string>();
    
    Object.entries(localRules).forEach(([categoryId, rule]) => {
      if (rule.status === 'enabled_with_rules') {
        const mainCondition = rule.conditions[0];
        if (!mainCondition?.ruleType || mainCondition.selectedTagIds.length === 0) {
          errors.add(categoryId);
        }
      }
    });
    
    setValidationErrors(errors);
    return errors.size === 0;
  };

  const handleSaveChanges = async () => {
    if (!validateRules()) {
      toast({
        title: "Validation Error",
        description: "Please fix the validation errors before saving.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    
    try {
      console.log('[handleSaveChanges] Saving changes with localRules:', localRules);
      
      // Convert local rules to the simplified format the hook expects
      const newAppFilter: Record<string, any> = {};
      
      categories.forEach(category => {
        const categoryId = category._id;
        if (!categoryId || typeof categoryId !== 'string') {
          console.error('[handleSaveChanges] Invalid category ID, skipping:', category.name, categoryId);
          return;
        }
        
        const rule = localRules[categoryId];
        console.log(`[handleSaveChanges] Processing category ${category.name} (ID: ${categoryId}) with rule:`, rule);
        
        if (rule) {
          if (rule.status === 'disabled') {
            // For disabled status, we don't include it in the appFilter at all
            // This is handled by the hook - categories not in the filter are considered disabled
            console.log(`[handleSaveChanges] ${category.name} is disabled, not including in filter`);
          } else if (rule.status === 'fully_enabled') {
            // For fully enabled, we pass the status directly
            newAppFilter[category.name] = {
              status: 'fully_enabled'
            };
            console.log(`[handleSaveChanges] ${category.name} set to fully enabled`);
          } else if (rule.status === 'enabled_with_rules') {
            const mainCondition = rule.conditions[0];
            newAppFilter[category.name] = {
              status: 'enabled_with_rules',
              selectedTagIds: mainCondition?.selectedTagIds || [],
              logicOperator: mainCondition?.logicOperator || 'any_of',
              exceptionTagIds: mainCondition?.exceptionTagIds || [],
              exceptionLogicOperator: mainCondition?.exceptionLogicOperator || 'any_of'
            };
            console.log(`[handleSaveChanges] ${category.name} set to enabled with rules:`, newAppFilter[category.name]);
          }
        }
      });

      console.log('[handleSaveChanges] Final appFilter for save:', newAppFilter);
      
      await updateAppFilter(newAppFilter, levelId);
      
      setIsEditMode(false);
      setValidationErrors(new Set());
      
    } catch (error) {
      console.error('[handleSaveChanges] Error:', error);
      // Error is already handled in the hook
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateRule = (categoryId: string, updates: Partial<CategoryRule>) => {
    console.log('[handleUpdateRule] Updating rule for category ID:', categoryId, 'with updates:', updates);
    
    // Validate categoryId before proceeding
    if (!categoryId || typeof categoryId !== 'string') {
      console.error('[handleUpdateRule] Invalid category ID:', categoryId);
      return;
    }
    
    setLocalRules(prev => {
      const currentRule = prev[categoryId];
      if (!currentRule) {
        // If no current rule exists, create a default one with required fields
        const defaultRule: CategoryRule = {
          category_id: categoryId,
          level_id: levelId,
          status: 'disabled',
          rule_type: undefined,
          selected_tag_ids: [],
          logic_operator: 'any_of',
          exception_tag_ids: [],
          exception_logic_operator: 'any_of',
          conditions: []
        };
        const updatedRule: CategoryRule = {
          ...defaultRule,
          ...updates
        };
        
        // If status is changing to enabled_with_rules and no conditions exist, create default condition
        if (updates.status === 'enabled_with_rules' && (!updatedRule.conditions || updatedRule.conditions.length === 0)) {
          updatedRule.conditions = [{
            id: 'main',
            ruleType: 'include_with',
            selectedTagIds: [],
            logicOperator: 'any_of',
            exceptionTagIds: [],
            exceptionLogicOperator: 'any_of'
          }];
        }
        
        console.log('[handleUpdateRule] Created new rule for category ID:', categoryId, updatedRule);
        return {
          ...prev,
          [categoryId]: updatedRule
        };
      }
      
      // Clean any Convex wrapped values from the current rule
      const cleanedCurrentRule = cleanConvexValue(currentRule);
      
      // Update existing rule while preserving required fields
      const updatedRule: CategoryRule = {
        ...cleanedCurrentRule,
        ...updates
      };
      
      // If status is changing to enabled_with_rules and no conditions exist, create default condition
      if (updates.status === 'enabled_with_rules' && (!updatedRule.conditions || updatedRule.conditions.length === 0)) {
        updatedRule.conditions = [{
          id: 'main',
          ruleType: 'include_with',
          selectedTagIds: [],
          logicOperator: 'any_of',
          exceptionTagIds: [],
          exceptionLogicOperator: 'any_of'
        }];
      }
      
      console.log('[handleUpdateRule] Updated rule for category ID:', categoryId, updatedRule);
      return {
        ...prev,
        [categoryId]: updatedRule
      };
    });
  };

  const handleAddCondition = (categoryId: string) => {
    const newCondition: TagCondition = {
      id: 'main',
      ruleType: 'include_with',
      selectedTagIds: [],
      logicOperator: 'any_of',
      exceptionTagIds: [],
      exceptionLogicOperator: 'any_of'
    };

    setLocalRules(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        conditions: [newCondition]
      }
    }));
  };

  const handleUpdateCondition = (categoryId: string, conditionId: string, updates: Partial<TagCondition>) => {
    setLocalRules(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        conditions: prev[categoryId].conditions.map(condition =>
          condition.id === conditionId ? { ...condition, ...updates } : condition
        )
      }
    }));
  };

  const handleRemoveCondition = (categoryId: string, conditionId: string) => {
    setLocalRules(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        conditions: prev[categoryId].conditions.filter(condition => condition.id !== conditionId)
      }
    }));
  };

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading categories...</div>
      </div>
    );
  }

  // Count categories with allowed status for display
  const allowedCategoriesCount = categories.filter(category => 
    getCategoryStatus(category.name) !== 'disabled'
  ).length;

  // Show empty state when no categories are allowed (not in edit mode)
  if (!isEditMode && allowedCategoriesCount === 0) {
    return (
      <div className="space-y-6">
        {/* Edit/Save buttons section */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">App Categories</h3>
            <p className="text-gray-600">Configure which app categories are allowed for this level</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleEditMode}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </div>
        </div>

        {/* Empty state */}
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="w-12 h-12 text-amber-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No App Categories Allowed</h3>
            <p className="text-gray-600 text-center mb-4 max-w-md">
              This level currently has no app categories enabled. Users assigned to this level will not be able to access any category-based apps.
            </p>
            <p className="text-sm text-gray-500 text-center mb-4">
              Click "Edit" above to configure which app categories should be allowed for this level.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Edit/Save buttons section */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">App Categories</h3>
          <p className="text-gray-600">Configure which app categories are allowed for this level</p>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveChanges} disabled={isSaving}>
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={handleEditMode}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Categories Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map(category => {
              const categoryId = category._id;
              if (!categoryId || typeof categoryId !== 'string') {
                console.error('[CategoryTable] Invalid category ID, skipping render:', category.name, categoryId);
                return null;
              }
              
              return (
                <CategoryTableRow
                  key={categoryId}
                  category={category}
                  rule={localRules[categoryId]}
                  isExpanded={expandedCategories.has(categoryId)}
                  onToggleExpanded={() => toggleCategoryExpansion(categoryId)}
                  onUpdateRule={handleUpdateRule}
                  onAddCondition={handleAddCondition}
                  onUpdateCondition={handleUpdateCondition}
                  onRemoveCondition={handleRemoveCondition}
                  hasValidationError={validationErrors.has(categoryId)}
                  isEditMode={isEditMode}
                />
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
